import axios from "axios";
import {
  CallCenterDocumentResponse,
  CitiesResponse,
  DocumentResponse,
  GetManagementDetailRequestResponse,
  GetManagementIdRequestBody,
  GetManagementIdRequestResponse,
  GetManagementsRequestBody,
  GetManagementsRequestResponse,
  LocationsResponse,
  MunicipalitiesResponse,
  PostCallCenterRequestBody,
  PostCallCenterResponseBody,
  ProvincesResponse,
} from "../../models/managements/managements.model";
import { getBase64 } from "../../../utils/components/managements/managements.util";
import { API_ENDPOINTS } from "../../../config/endpoints.config";

const URL = "https://vzh38qwvoj.execute-api.us-east-1.amazonaws.com/dev";

const getManagements = async (
  requestBody: GetManagementsRequestBody,
  token: string,
  abortSignal: AbortSignal
): Promise<GetManagementsRequestResponse> =>
  await axios
    .post(API_ENDPOINTS.CLIENT_MANAGEMENT, requestBody, {
      headers: {
        Authorization: token,
      },
      signal: abortSignal,
    })
    .then(({ data }) => data)
    .catch((error) => {
      throw error;
    });

const getManagementId = async (
  requestBody: GetManagementIdRequestBody,
  token: string,
  idGestion: string
): Promise<GetManagementIdRequestResponse> =>
  await axios
    .post(
      `${URL}/client/management/${idGestion}
    `,
      requestBody,
      {
        headers: {
          Authorization: token,
        },
      }
    )
    .then(({ data }) => data)
    .catch((error) => {
      throw error;
    });

const postCallCenter = async (
  requestBody: PostCallCenterRequestBody,
  token: string
): Promise<PostCallCenterResponseBody[]> =>
  await axios
    .post(API_ENDPOINTS.CLIENT_CALLCENTER_MANAGEMENT, requestBody, {
      headers: {
        Authorization: token,
      },
    })
    .then(({ data }) => data)
    .catch((error) => {
      throw error;
    });

const getManagementDetail = async (
  issueNumber: number,
  token: string,
  abortSignal: AbortSignal
): Promise<GetManagementDetailRequestResponse> =>
  await axios
    .post(
      API_ENDPOINTS.CLIENT_MANAGEMENT_DETAIL,
      { NumeroIncidencia: issueNumber },
      {
        headers: {
          Authorization: token,
        },
        signal: abortSignal,
      }
    )
    .then(({ data }) => data)
    .catch((error) => {
      throw error;
    });

const getServicesDetail = async (
  issueNumber: number,
  token: string,
  abortSignal: AbortSignal
): Promise<GetManagementDetailRequestResponse[]> =>
  await axios
    .post(
      API_ENDPOINTS.CLIENT_MANAGEMENT_DETAIL,
      { NumeroIncidencia: issueNumber },
      {
        headers: {
          Authorization: token,
        },
        signal: abortSignal,
      }
    )
    .then(({ data }) => data)
    .catch((error) => {
      throw error;
    });

const downloadDocument = async (
  documentType: string
): Promise<DocumentResponse> => {
  const response = await axios.get<DocumentResponse>(
    API_ENDPOINTS.CLIENT_DOCUMENT,
    {
      params: {
        documentType: documentType,
      },
    }
  );

  return response.data;
};

const getLocations = async (token: string): Promise<LocationsResponse[]> =>
  await axios
    .post(
      API_ENDPOINTS.LOCATIONS,
      {},
      {
        headers: {
          Authorization: token,
        },
      }
    )
    .then(({ data }) =>
      data.map((element: any) => ({
        id: element.Id,
        name: element.Nombre,
        ine: element.Ine,
      }))
    )
    .catch((error) => {
      throw error;
    });

const getProvinces = async (token: string): Promise<ProvincesResponse[]> =>
  await axios
    .post(
      API_ENDPOINTS.LOCATIONS_PROVINCES,
      {},
      {
        headers: {
          Authorization: token,
        },
      }
    )
    .then(({ data }) =>
      data.map((element: any) => ({
        id: element.Id,
        name: element.Nombre,
        ine: element.Ine,
      }))
    )
    .catch((error) => {
      throw error;
    });

const getMunicipalities = async (
  token: string,
  provinceId: number
): Promise<MunicipalitiesResponse[]> =>
  await axios
    .post(
      API_ENDPOINTS.LOCATIONS_MUNICIPALITIES,
      {
        provinceId: provinceId,
      },
      {
        headers: {
          Authorization: token,
        },
      }
    )
    .then(({ data }) =>
      data.map((element: any) => ({
        id: element.Id,
        name: element.Nombre,
        ine: element.Ine,
      }))
    )
    .catch((error) => {
      throw error;
    });

const getCities = async (
  token: string,
  provinceId: number,
  municipalityId: number
): Promise<CitiesResponse[]> =>
  await axios
    .post(
      API_ENDPOINTS.LOCATIONS_CITIES,
      {
        provinceId: provinceId,
        municipalityId: municipalityId,
      },
      {
        headers: {
          Authorization: token,
        },
      }
    )
    .then(({ data }) =>
      data.map((element: any) => ({
        id: element.Id,
        name: element.Nombre,
        ine: element.Ine,
      }))
    )
    .catch((error) => {
      throw error;
    });

const getCallCenterDocuments = async (
  idCallCenter: number,
  token: string
): Promise<CallCenterDocumentResponse[]> =>
  await axios
    .post(
      API_ENDPOINTS.CLIENT_MANAGEMENT_ID_DOCUMENTS(idCallCenter.toString()),
      {},
      {
        headers: {
          Authorization: token,
        },
      }
    )
    .then(({ data }) => data)
    .catch((error) => {
      throw error;
    });

const postImportDocument = async (
  idGestion: number,
  idCarpeta: number,
  file: File,
  token: string
): Promise<void> =>
  getBase64(file)
    .then((res) => ({
      IdCallCenter: idGestion,
      IdCarpetaIncidencias: idCarpeta,
      Documento: res as string,
      NombreDocumento: file.name,
    }))
    .then(
      async (requestBody) =>
        await axios.post(API_ENDPOINTS.CLIENT_CUPS_DOCUMENT, requestBody, {
          headers: {
            Authorization: token,
          },
        })
    );

export {
  getManagements,
  getManagementId,
  postCallCenter,
  getManagementDetail,
  getServicesDetail,
  postImportDocument,
  downloadDocument,
  getCallCenterDocuments,
  getLocations,
  getProvinces,
  getMunicipalities,
  getCities,
};
